// ─────────────────────────────────────────────────────────────────
// Géométrie des courbes d'historique.
//
// Isolée du composant et sans dépendance à React : c'est ce qui permet de
// vérifier les tracés — bornes, position du dernier point, graduations — sans
// navigateur ni capture d'écran.
// ─────────────────────────────────────────────────────────────────

// Marges du viewBox. Elles ne sont pas décoratives : sans place à gauche pour
// les graduations et en bas pour les semaines, les étiquettes sortent du cadre.
export const MARGES = { haut: 14, droite: 14, bas: 26, gauche: 38 };

export function calculerEchelle(valeurs, { min: minImpose, max: maxImpose } = {}) {
    const connues = valeurs.filter((v) => v !== null && v !== undefined && Number.isFinite(v));
    if (connues.length === 0) return null;

    let min = minImpose ?? Math.min(...connues);
    let max = maxImpose ?? Math.max(...connues);

    // Une série plate — deux semaines au même taux — donnerait une hauteur nulle
    // et un tracé collé au bord. On ouvre alors une fenêtre autour de la valeur.
    if (min === max) {
        const marge = Math.max(Math.abs(min) * 0.05, 1);
        min -= marge;
        max += marge;
    } else {
        const respiration = (max - min) * 0.12;
        if (minImpose === undefined) min -= respiration;
        if (maxImpose === undefined) max += respiration;
    }

    return { min, max };
}

// Trois graduations : le minimum, le milieu, le maximum. Chacune nomme une
// valeur que la courbe atteint réellement dans sa fenêtre.
export function graduations(echelle, formater = (v) => Math.round(v)) {
    if (!echelle) return [];
    const { min, max } = echelle;
    return [max, (max + min) / 2, min].map((valeur) => ({
        valeur,
        libelle: formater(valeur),
        part: (max - valeur) / (max - min),
    }));
}

export function tracerCourbe(points, echelle, { largeur, hauteur }) {
    if (!echelle || points.length === 0) return { ligne: "", aire: "", marqueurs: [] };

    const dedans = { largeur: largeur - MARGES.gauche - MARGES.droite, hauteur: hauteur - MARGES.haut - MARGES.bas };
    const pas = points.length > 1 ? dedans.largeur / (points.length - 1) : 0;

    const marqueurs = points.map((point, index) => ({
        ...point,
        x: MARGES.gauche + (points.length > 1 ? index * pas : dedans.largeur / 2),
        y: MARGES.haut + dedans.hauteur * (1 - (point.valeur - echelle.min) / (echelle.max - echelle.min)),
        connu: point.valeur !== null && point.valeur !== undefined && Number.isFinite(point.valeur),
    }));

    const tracables = marqueurs.filter((m) => m.connu);
    if (tracables.length === 0) return { ligne: "", aire: "", marqueurs };

    const ligne = tracables.map((m, i) => `${i === 0 ? "M" : "L"}${m.x.toFixed(1)} ${m.y.toFixed(1)}`).join(" ");
    const bas = MARGES.haut + dedans.hauteur;
    const aire = `${ligne} L${tracables[tracables.length - 1].x.toFixed(1)} ${bas.toFixed(1)} L${tracables[0].x.toFixed(1)} ${bas.toFixed(1)} Z`;

    return { ligne, aire, marqueurs };
}

// La variation entre le premier et le dernier point connus. C'est le chiffre qui
// répond à « ça s'améliore ou pas », et il n'a de sens qu'à partir de deux points.
export function variation(points) {
    const connus = points.filter((p) => p.valeur !== null && p.valeur !== undefined && Number.isFinite(p.valeur));
    if (connus.length < 2) return null;
    const ecart = connus[connus.length - 1].valeur - connus[0].valeur;
    return { ecart, depuis: connus[0].semaine, jusqua: connus[connus.length - 1].semaine };
}

// Ton d'une tendance. `sensFavorable` vaut +1 quand la mesure doit monter (un
// taux de conformité) et -1 quand elle doit baisser (un score d'exposition).
//
// C'est le SENS qui décide de la couleur, jamais le signe seul : sans ça, une
// exposition qui se dégrade s'afficherait en vert au motif que son chiffre
// augmente.
export function tonTendance(evolution, sensFavorable) {
    if (!evolution || evolution.ecart === 0) return 'accent';
    return Math.sign(evolution.ecart) === sensFavorable ? 'ok' : 'danger';
}
